import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import { useFiltersContext } from '@openx/components-filters/lib/context/FiltersContext';
import { FiltersBar } from '@openx/components-filters/lib/FiltersBar/FiltersBar';
import { type KeyboardEventHandler, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { type PromptMode, promptModes } from '../../constants';
import { useButtonLabel } from '../../hooks/useButtonLabel';
import { useDealPrompt } from '../../hooks/useDealPrompt';
import { useFilterChangeDetection } from '../../hooks/useFilterChangeDetection';
import { usePromptInputDialog } from '../../hooks/usePromptInputDialog';
import { dealPromptContextTypes } from '../../utils/types';
import { DealPromptAudienceComposition } from '../DealPromptAudienceComposition/DealPromptAudienceComposition';
import { DealPromptEditPromptDialog } from '../DealPromptEditPromptDialog';
import { ActionButtons } from './ActionButtons';
import { LoadingIndicator } from './LoadingIndicator';
import { PromptInputField } from './PromptInputField';
import { PromptPreview } from './PromptPreview';
import { RegenerateButton } from './RegenerateButton';

type DealPromptPromptInputProps = {
  value: string;
  onChange: (val: string) => void;
  onSubmit: (searchOnly?: boolean) => void;
  onCancel: () => void;
  disabled?: boolean;
  loading?: boolean;
  mode?: PromptMode;
  isDirty?: boolean;
  hasNoResults?: boolean;
};

export const DealPromptPromptInput = ({
  value,
  onChange,
  onSubmit,
  onCancel,
  disabled = false,
  loading = false,
  mode = promptModes.DEFAULT_MODE,
  isDirty = false,
  hasNoResults = false,
}: DealPromptPromptInputProps) => {
  const { t } = useTranslation();

  const { criteria, setIsFilteringDisabled } = useFiltersContext();
  const { state } = useDealPrompt();
  const { contextType, hasError } = state;
  const { isOpen: isEditDialogOpen, openDialog: openEditDialog, closeDialog: closeEditDialog } = usePromptInputDialog();
  const buttonLabel = useButtonLabel(mode);
  const isPreview = mode === promptModes.PREVIEW_MODE && !loading;
  const isAudienceError = contextType === dealPromptContextTypes.AUDIENCE && hasError;
  const shouldUseAudienceSearchOnly = contextType === dealPromptContextTypes.AUDIENCE && !isDirty && !hasNoResults;

  const shouldDisplayAlert = isPreview && Boolean(state.hasDifferentFiltersThanWissp);

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = useCallback(
    e => {
      if (isPreview) return;
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        if (value.trim()) {
          onSubmit();
        }
      }
    },
    [isPreview, onSubmit, value]
  );

  const handleEditPrompt = useCallback(() => {
    openEditDialog();
  }, [openEditDialog]);

  useEffect(() => {
    setIsFilteringDisabled(loading);
  }, [loading, setIsFilteringDisabled]);

  const isFilterChangeDetected = useFilterChangeDetection({
    isPreview,
  });

  const isSearchDisabled = disabled || loading;
  const isRegenerateDisabled = isSearchDisabled || isAudienceError || (!isDirty && !isFilterChangeDetected);

  const handleSubmit = useCallback(
    (searchOnly?: boolean) => {
      onSubmit(searchOnly);
    },
    [onSubmit]
  );

  const handleRegenerate = useCallback(() => {
    handleSubmit(false);
  }, [handleSubmit]);

  const handleRegenerateButtonClick = useCallback(() => {
    handleSubmit(shouldUseAudienceSearchOnly);
  }, [handleSubmit, shouldUseAudienceSearchOnly]);

  return (
    <Box>
      {isEditDialogOpen ? (
        <DealPromptEditPromptDialog
          open={isEditDialogOpen}
          onClose={closeEditDialog}
          prompt={value}
          onPromptChange={onChange}
          onRegenerate={handleRegenerate}
          contextType={contextType}
        />
      ) : null}
      <Box sx={{ position: 'relative', width: '100%' }}>
        {!isPreview ? (
          <PromptInputField value={value} onChange={onChange} onKeyDown={handleKeyDown} disabled={disabled} />
        ) : (
          <PromptPreview value={value} />
        )}

        <Stack sx={{ position: 'absolute', left: 16, right: 16, bottom: 12, gap: 1 }}>
          <LoadingIndicator visible={loading} />
          <ActionButtons
            isPreview={isPreview}
            loading={loading}
            disabled={disabled}
            value={value}
            buttonLabel={buttonLabel}
            onCancel={onCancel}
            onPrimaryClick={handleRegenerate}
            onEditPrompt={handleEditPrompt}
            criteria={criteria}
            contextType={contextType}
          />
        </Stack>
      </Box>
      <Stack
        direction="column"
        sx={[
          {
            gap: 1.5,
          },
          !isPreview
            ? { width: '100%', mt: 1.5, ml: -2 }
            : {
                width: '100%',
                boxShadow: '0px 1px 3px 0px rgba(0,0,0,0.05), 0px 8px 16px -4px rgba(0,0,0,0.08)',
                mt: 1.5,
                p: 1.5,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '2px',
              },
        ]}
      >
        <Box sx={{ ml: isPreview ? -2 : 0 }}>
          <FiltersBar />
        </Box>
        {mode === promptModes.PREVIEW_MODE && contextType === dealPromptContextTypes.AUDIENCE && (
          <DealPromptAudienceComposition />
        )}

        {isPreview ? (
          <>
            <Divider />
            {shouldDisplayAlert ? (
              <Box sx={{ pl: 2 }}>
                <Alert severity="warning" data-test="deal-prompt-filters-warning">
                  {t(
                    'The filters you applied are different from those generated by AI based on your prompt. For now, both sets of filters are applied.'
                  )}
                  {!isRegenerateDisabled ? ` ${t('Click ‘Regenerate’ to refresh the results.')}` : null}
                </Alert>
              </Box>
            ) : null}
            <RegenerateButton
              loading={loading}
              disabled={isRegenerateDisabled}
              buttonLabel={buttonLabel}
              onClick={handleRegenerateButtonClick}
              contextType={contextType}
            />
          </>
        ) : null}
      </Stack>
    </Box>
  );
};
